import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

export interface IVariant {
  variant?: 'unanswered' | 'answered' | 'current' | 'review';
}

export interface IQuestionButtonProps extends IVariant {
  number?: number;
  className?: string;
  onClick?: (number: number) => void;
  legend?: boolean;
  isMarked?: boolean;
}

const QuestionButton = ({
  variant = 'current',
  legend = false,
  isMarked = false,
  number = 0,
  onClick,
  className = '',
}: IQuestionButtonProps) => {
  const { question } = useParams();
  const answerMarked = isMarked && variant === 'answered';
  const gradientStyles = answerMarked ? `hard-gradient` : '';

  // `grid-cols-7` at 390px gives (390 - 32 - 6*8) / 7 ~= 44px per cell, which is
  // exactly the DESIGN minimum touch target, so the buttons fill the cell.
  const sizeStyles = legend ? 'w-8 h-4' : 'w-9 h-10 m:w-full m:h-11';

  const classNames = `relative flex items-center justify-center rounded-xs 
  font-medium text-heading ${sizeStyles} ${VARIANTS[variant]} ${gradientStyles} ${className}`;

  const handleClick = useCallback(() => {
    if (onClick) onClick(number);
  }, [number, onClick]);

  if (legend) {
    return (
      <span className={classNames}>
        <Decoration variant={variant} />
      </span>
    );
  }

  return (
    <button onClick={handleClick} className={classNames} disabled={Number(question) == number}>
      <Decoration variant={variant} />
      {number}
    </button>
  );
};

const Decoration = ({ variant }: IVariant) => {
  if (variant === 'answered') {
    return <span className="absolute w-2 h-2 -top-1 bg-neutral2 rotate-45 m:w-3 m:h-3" />;
  }

  if (variant === 'review') {
    return <span className="w-2.5 h-2.5 absolute -top-[0.3125rem] bg-warning border-2 border-neutral rounded-full
                          m:w-3 m:h-3" />;
  }

  return null;
};

const VARIANTS = {
  unanswered:
  'bg-neutral2-canvas text-neutral2-canvas-text hover:bg-neutral2-canvas-hover hover:text-neutral2-canvas-hover-text',
  answered: 'bg-info text-info-text hover:bg-info-hover hover:text-info-hover-text',
  current: 'bg-success text-success-text hover:bg-success-hover hover:text-success-hover-text',
  review: 'bg-danger text-danger-text hover:bg-danger-hover hover:text-danger-hover-text',
};

export default QuestionButton;
